import { useCallback, useEffect, useRef, useState } from 'react'
import { io, type Socket } from 'socket.io-client'
import { IPerioChart } from '@dataTypes/chartingFeature.types'
import {
  StreamingVoicePhase,
  VOICE_INACTIVITY_TIMEOUT_MS,
} from '@hooks/useStreamingVoiceSession'
import {
  useVoiceTranscriptFeed,
  VoiceTranscriptEntry,
  VoiceTranscriptSnapshot,
} from '@hooks/useVoiceTranscriptFeed'
import { getAccessToken } from '@services/auth/accessTokenStore'
import { getEnv } from '@utils/env'
import { pcmToBase64 } from '@utils/perio-audio.utils'
import {
  createKeywordState,
  KeywordState,
  PerioVoiceHighlight,
  VoiceCursor,
} from '@utils/perio-keyword.utils'

type StopOptions = {
  preserveMessage?: boolean
  message?: string
}

type PerioVoiceKeyword = Omit<KeywordState, 'history'> & {
  history?: KeywordState['history']
}

type PerioVoiceStep = PerioVoiceHighlight & { chart: IPerioChart }

type PerioVoiceReadyPayload = {
  sessionId: string
  model: string
  keyword: PerioVoiceKeyword
  message: string
}

type PerioVoiceTranscriptPayload = {
  sessionId: string
  itemId: string
  text: string
  formatted?: string
  interpreted?: string
}

type PerioVoiceChartPatchPayload = {
  sessionId: string
  chart: IPerioChart
  keyword: PerioVoiceKeyword
  message: string
  focus?: PerioVoiceHighlight
  steps?: PerioVoiceStep[]
  control?: string
}

type PerioVoiceMessagePayload = {
  sessionId: string
  message: string
  keyword?: PerioVoiceKeyword
}

type PerioVoiceErrorPayload = {
  sessionId?: string
  message: string
  code?: string
}

const chartingBase = (): string => {
  const base = getEnv('REACT_APP_BUSINESS_LAYER_CHARTING').replace(/\/$/, '')
  if (!base) throw new Error('The charting service URL is not configured.')
  return base
}

const createSessionId = (): string => {
  if (typeof crypto !== 'undefined' && 'randomUUID' in crypto) {
    return crypto.randomUUID()
  }
  return `perio_voice_${Date.now()}_${Math.random().toString(36).slice(2)}`
}

const asKeyword = (keyword: PerioVoiceKeyword): KeywordState => ({
  cursor: keyword.cursor,
  pending: keyword.pending,
  ...(keyword.pendingBefore !== undefined
    ? { pendingBefore: keyword.pendingBefore }
    : {}),
  paused: keyword.paused,
  ended: keyword.ended,
  history: keyword.history ?? [],
})

export function usePerioStreamingVoice({
  enabled,
  contextKey,
  chart,
  onChart,
}: {
  enabled: boolean
  contextKey: string
  chart: IPerioChart
  onChart: (next: IPerioChart) => void
}): {
  phase: StreamingVoicePhase
  message: string
  toggle: () => void
  stop: () => void
  keyword: KeywordState
  backlog: number
  recognized: string
  liveText: string
  history: VoiceTranscriptEntry[]
  queued: string[]
  subscribe: (listener: () => void) => () => void
  getSnapshot: () => VoiceTranscriptSnapshot
  viewCursor?: PerioVoiceHighlight
  selectCursor: (patch: Partial<VoiceCursor>) => void
} {
  const [phase, setPhase] = useState<StreamingVoicePhase>('idle')
  const [message, setMessage] = useState('')
  const [keyword, setKeyword] = useState(createKeywordState)
  const [backlog, setBacklog] = useState(0)
  const feed = useVoiceTranscriptFeed()
  const feedRef = useRef(feed)
  feedRef.current = feed
  const [viewCursor, setViewCursor] = useState<PerioVoiceHighlight>()
  const keywordRef = useRef(keyword)
  const viewCursorRef = useRef(viewCursor)
  const latest = useRef({ chart, enabled, contextKey, onChart })
  latest.current = { chart, enabled, contextKey, onChart }
  const sentChart = useRef('')
  const stepToken = useRef(0)
  const generation = useRef(0)
  const running = useRef(false)
  const resources = useRef<{
    stream?: MediaStream
    audio?: AudioContext
    node?: AudioWorkletNode
    socket?: Socket
    sessionId?: string
    inactivityWatchdog?: ReturnType<typeof setInterval>
  }>({})

  const updateViewCursor = useCallback(
    (next: PerioVoiceHighlight | undefined): void => {
      viewCursorRef.current = next
      setViewCursor(next)
    },
    [],
  )
  const updateKeyword = useCallback((next: KeywordState): void => {
    keywordRef.current = next
    setKeyword(next)
  }, [])

  const stop = useCallback(
    (options?: StopOptions): void => {
      const wasRunning = running.current
      generation.current++
      running.current = false
      if (!options?.preserveMessage) stepToken.current++
      const old = resources.current
      resources.current = {}
      if (old.socket && old.sessionId) {
        old.socket.emit('perio_voice:stop', { sessionId: old.sessionId })
      }
      old.socket?.removeAllListeners()
      old.socket?.disconnect()
      clearInterval(old.inactivityWatchdog)
      old.node?.disconnect()
      old.stream?.getTracks().forEach((track) => track.stop())
      void old.audio?.close()
      feedRef.current.discard()
      setBacklog(0)
      if (wasRunning) {
        updateKeyword({ ...keywordRef.current, paused: false, pending: [] })
        setPhase('idle')
        if (!options?.preserveMessage) {
          setMessage(
            options?.message ??
              'Stopped. Any unfinished phrase was not applied.',
          )
        }
      }
    },
    [updateKeyword],
  )

  useEffect(() => {
    return (): void => stop()
  }, [contextKey, enabled, stop])

  useEffect(() => {
    feedRef.current.reset()
    setMessage('')
    updateViewCursor(undefined)
    updateKeyword(createKeywordState())
  }, [contextKey, updateKeyword, updateViewCursor])

  useEffect(() => {
    const socket = resources.current.socket
    const sessionId = resources.current.sessionId
    if (!running.current || !socket || !sessionId) return
    const serialized = JSON.stringify(chart.perio_data)
    if (serialized === sentChart.current) return
    sentChart.current = serialized
    socket.emit('perio_voice:chart.update', { sessionId, chart })
  }, [chart])

  const publishChart = useCallback((next: IPerioChart): void => {
    sentChart.current = JSON.stringify(next.perio_data)
    latest.current.onChart(next)
  }, [])

  const start = async (): Promise<void> => {
    if (!latest.current.enabled || running.current) return
    running.current = true
    feedRef.current.reset()
    setBacklog(0)
    updateKeyword(createKeywordState(keywordRef.current.cursor))
    const gen = ++generation.current
    const key = latest.current.contextKey
    const sessionId = createSessionId()
    let proxyReady = false
    let lastVoiceDetectedAt = Date.now()
    const valid = (): boolean =>
      gen === generation.current &&
      latest.current.enabled &&
      key === latest.current.contextKey
    const fail = (error: unknown): void => {
      if (!valid()) return
      const reason =
        error instanceof Error
          ? error.message
          : 'Unable to connect streaming transcription.'
      stop()
      setPhase('error')
      setMessage(reason)
    }
    const playSteps = async (
      steps: PerioVoiceStep[],
      payload: PerioVoiceChartPatchPayload,
    ): Promise<void> => {
      const token = ++stepToken.current
      for (let index = 0; index < steps.length; index++) {
        if (token !== stepToken.current) return
        const step = steps[index]
        updateViewCursor({
          tooth: step.tooth,
          section: step.section,
          metric: step.metric,
        })
        updateKeyword({
          ...asKeyword(payload.keyword),
          cursor: {
            ...payload.keyword.cursor,
            tooth: step.tooth,
            section: step.section,
          },
        })
        publishChart(step.chart)
        if (index < steps.length - 1) {
          await new Promise<void>((resolve) => setTimeout(resolve, 300))
        }
      }
      if (token !== stepToken.current) return
      publishChart(payload.chart)
      updateKeyword(asKeyword(payload.keyword))
      updateViewCursor(payload.focus)
    }

    setPhase('connecting')
    setMessage('Connecting streaming transcription...')
    try {
      if (!navigator.mediaDevices?.getUserMedia) {
        throw new Error(
          'Microphone capture is unavailable. Use a supported browser over HTTPS (or localhost for development).',
        )
      }
      const token = getAccessToken()
      const dbName = localStorage.getItem('db_name')
      if (!token) throw new Error('Sign in again before using Perio voice.')
      const socket = io(chartingBase(), {
        path: '/api/v1/charting/socket.io/',
        transports: ['websocket'],
        reconnection: false,
        autoConnect: false,
        auth: { token, dbName: dbName ?? undefined },
      })
      resources.current.socket = socket
      resources.current.sessionId = sessionId

      socket.on('connect_error', (error) => fail(error))
      socket.on('disconnect', (reason) => {
        if (running.current && valid()) {
          fail(new Error(`Voice connection closed: ${reason}`))
        }
      })
      socket.on('perio_voice:ready', (payload: PerioVoiceReadyPayload) => {
        if (!valid() || payload.sessionId !== sessionId) return
        proxyReady = true
        lastVoiceDetectedAt = Date.now()
        updateKeyword(asKeyword(payload.keyword))
        const current = latest.current.chart
        sentChart.current = JSON.stringify(current.perio_data)
        socket.emit('perio_voice:chart.update', { sessionId, chart: current })
        setPhase('listening')
        setMessage(payload.message)
      })
      socket.on(
        'perio_voice:partial',
        (payload: PerioVoiceTranscriptPayload) => {
          if (!valid() || payload.sessionId !== sessionId) return
          feedRef.current.partial(payload.itemId, payload.text)
        },
      )
      socket.on('perio_voice:final', (payload: PerioVoiceTranscriptPayload) => {
        if (!valid() || payload.sessionId !== sessionId) return
        feedRef.current.finalize(
          payload.itemId,
          payload.interpreted ?? payload.text,
          { heard: payload.text },
        )
        setBacklog((value) => Math.max(0, value - 1))
      })
      socket.on('perio_voice:message', (payload: PerioVoiceMessagePayload) => {
        if (!valid() || payload.sessionId !== sessionId) return
        setMessage(payload.message)
        feedRef.current.annotate({ note: payload.message, status: 'failed' })
        if (payload.keyword) updateKeyword(asKeyword(payload.keyword))
      })
      socket.on(
        'perio_voice:chart_patch',
        (payload: PerioVoiceChartPatchPayload) => {
          if (!valid() || payload.sessionId !== sessionId) return
          setMessage(payload.message)
          feedRef.current.annotate({ note: payload.message, status: 'applied' })
          if (payload.steps?.length) {
            void playSteps(payload.steps, payload)
            return
          }
          stepToken.current++
          publishChart(payload.chart)
          updateKeyword(asKeyword(payload.keyword))
          updateViewCursor(payload.focus)
        },
      )
      socket.on('perio_voice:error', (payload: PerioVoiceErrorPayload) => {
        if (!valid()) return
        if (payload.sessionId && payload.sessionId !== sessionId) return
        fail(new Error(payload.message))
      })
      socket.on(
        'perio_voice:stopped',
        (payload: { sessionId: string; reason: string }) => {
          if (!valid() || payload.sessionId !== sessionId) return
          stop({
            preserveMessage: payload.reason === 'voice-command',
            message: 'Microphone stopped. Click the microphone to start again.',
          })
        },
      )

      const stream = await navigator.mediaDevices.getUserMedia({
        audio: {
          channelCount: 1,
          echoCancellation: true,
          noiseSuppression: true,
        },
      })
      if (!valid()) {
        stream.getTracks().forEach((track) => track.stop())
        return
      }
      resources.current.stream = stream
      const audio = new AudioContext({ sampleRate: 24000 })
      resources.current.audio = audio
      await audio.audioWorklet.addModule('/perio-stream-capture.js')
      if (!valid()) return
      if (audio.sampleRate !== 24000) {
        throw new Error(
          'This browser cannot capture at the required sample rate.',
        )
      }
      socket.on('connect', () => {
        if (!valid()) return
        const current = latest.current.chart
        sentChart.current = JSON.stringify(current.perio_data)
        socket.emit('perio_voice:start', {
          sessionId,
          patient_id: current.patient_id,
          clinic_id: current.clinic_id,
          chart: current,
          cursor: keywordRef.current.cursor,
        })
      })
      const node = new AudioWorkletNode(audio, 'perio-stream-capture')
      resources.current.node = node
      node.onprocessorerror = (): void =>
        fail(
          new Error(
            'Audio capture failed. Restart and repeat unfinished speech.',
          ),
        )
      const mute = audio.createGain()
      mute.gain.value = 0
      node.port.onmessage = (
        event: MessageEvent<{
          type: string
          audio?: Float32Array
          error?: string
        }>,
      ): void => {
        if (!valid() || !proxyReady) return
        try {
          if (event.data.type === 'error') throw new Error(event.data.error)
          if (event.data.type === 'start') {
            lastVoiceDetectedAt = Date.now()
            socket.emit('perio_voice:turn', { sessionId })
          } else if (event.data.type === 'audio' && event.data.audio) {
            socket.emit('perio_voice:audio.append', {
              sessionId,
              audio: pcmToBase64(event.data.audio),
            })
          } else if (event.data.type === 'commit') {
            setBacklog((value) => value + 1)
            socket.emit('perio_voice:audio.commit', { sessionId })
          }
        } catch (error) {
          fail(error)
        }
      }
      audio.createMediaStreamSource(stream).connect(node)
      node.connect(mute).connect(audio.destination)
      await audio.resume()
      socket.connect()
      resources.current.inactivityWatchdog = setInterval(() => {
        if (
          valid() &&
          proxyReady &&
          Date.now() - lastVoiceDetectedAt >= VOICE_INACTIVITY_TIMEOUT_MS
        ) {
          stop({
            message:
              'Microphone stopped after 3 minutes without detected speech. Click the microphone to start again.',
          })
        }
      }, 1000)
      stream.getTracks().forEach((track) => {
        track.onended = (): void =>
          fail(new Error('Microphone disconnected. Restart to continue.'))
      })
    } catch (error) {
      fail(error)
    }
  }

  const toggle = (): void => {
    if (running.current) stop()
    else void start()
  }

  const selectCursor = useCallback(
    (patch: Partial<VoiceCursor>): void => {
      const nextCursor = { ...keywordRef.current.cursor, ...patch }
      const previous = keywordRef.current.cursor
      if (
        previous.tooth === nextCursor.tooth &&
        previous.section === nextCursor.section &&
        previous.metric === nextCursor.metric
      )
        return
      stepToken.current++
      updateKeyword({
        ...keywordRef.current,
        cursor: nextCursor,
        pending: [],
        pendingBefore: undefined,
        ended: false,
      })
      const wasRunning = running.current
      updateViewCursor(
        wasRunning
          ? {
              tooth: nextCursor.tooth,
              section: nextCursor.section,
              metric: nextCursor.metric,
            }
          : undefined,
      )
      feedRef.current.discard()
      setBacklog(0)
      const sessionId = resources.current.sessionId
      if (wasRunning && resources.current.socket && sessionId) {
        resources.current.socket.emit('perio_voice:cursor.update', {
          sessionId,
          tooth: nextCursor.tooth,
          section: nextCursor.section,
          metric: nextCursor.metric,
        })
        setMessage(
          `Position changed to tooth ${nextCursor.tooth} ${nextCursor.section} ${nextCursor.metric}. ` +
            'Unfinished speech was discarded. Still listening — pause briefly and repeat any unfinished phrase.',
        )
      }
    },
    [updateKeyword, updateViewCursor],
  )

  return {
    phase,
    message,
    toggle,
    stop,
    keyword,
    backlog,
    get recognized(): string {
      return feed.recognized
    },
    get liveText(): string {
      return feed.liveText
    },
    get history(): VoiceTranscriptEntry[] {
      return feed.history
    },
    get queued(): string[] {
      return feed.queued
    },
    subscribe: feed.subscribe,
    getSnapshot: feed.getSnapshot,
    viewCursor,
    selectCursor,
  }
}
